// src/components/ui/ChoixDate.tsx — SCE Issues
// Choisir une date (14/10, Guillaume : « améliore le calendrier de sélection de
// dates ») : remplace le champ date du navigateur, petit et peu lisible sous
// Windows, dans les fenêtres FNC-T.
//
//  - le champ montre la date en clair (« mer. 28 oct. 2026 ») et l'écart
//    (« dans 10 jours ») ;
//  - le calendrier : le mois en grille, semaine du lundi, aujourd'hui cerclé, le
//    jour choisi en jaune (la couleur des interactions), week-ends et jours fériés
//    en gris (un férié le dit au survol), dates hors bornes inactives ;
//  - des raccourcis selon le champ (« 10 j ouvrés », « 2 sem. », « Aujourd'hui ») ;
//  - au clavier : flèches (jour, semaine), Page préc. / suiv. (mois), Entrée,
//    Échap (ferme le calendrier seul, pas la fenêtre dessous).
// Règles : utils/calendrier.

import { useEffect, useMemo, useRef, useState, type CSSProperties, type JSX, type KeyboardEvent } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import type { Jour } from '@/types/fnct.types'
import { aujourdhuiParis, joursFeries } from '@/utils/fnct'
import {
  INITIALES_JOURS,
  decalerJours,
  decalerMois,
  ecartLisible,
  grilleMois,
  horsBornes,
  libelleDate,
  rangDansSemaine,
  titreMois,
} from '@/utils/calendrier'
import { Popover } from './Popover'

export interface RaccourciDate {
  libelle: string
  jour: Jour
}

export interface ChoixDateProps {
  id?: string
  /** Ce qu'on choisit (« Échéance ») : lecteurs d'écran, titre du calendrier. */
  libelle: string
  valeur: Jour | null
  onChange: (j: Jour | null) => void
  min?: Jour | null
  max?: Jour | null
  raccourcis?: readonly RaccourciDate[]
  /** Une croix pour vider le champ. */
  effacable?: boolean
  desactive?: boolean
  /** Au-dessus d'une grande fenêtre (81) ou d'une fenêtre NC-S / FNC-T (64+). */
  zIndex?: number
  style?: CSSProperties
}

export function ChoixDate({ id, libelle, valeur, onChange, min, max, raccourcis = [], effacable, desactive, zIndex = 95, style }: ChoixDateProps): JSX.Element {
  const [ancre, setAncre] = useState<HTMLButtonElement | null>(null)
  const [ouvert, setOuvert] = useState(false)
  const aujourdhui = aujourdhuiParis()
  return (
    <span className="inline-flex items-stretch" style={{ position: 'relative', width: '100%', ...style }}>
      <button
        id={id}
        ref={setAncre}
        type="button"
        disabled={desactive}
        aria-haspopup="dialog"
        aria-expanded={ouvert}
        aria-label={`${libelle} : ${valeur ? libelleDate(valeur) : 'à choisir'}`}
        onClick={() => setOuvert((o) => !o)}
        className="inline-flex items-center gap-2 text-left"
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 44,
          padding: '4px 12px',
          borderRadius: 8,
          border: `1px solid ${ouvert ? 'var(--color-black)' : 'var(--color-border)'}`,
          backgroundColor: 'var(--color-white)',
          fontFamily: 'inherit',
          cursor: desactive ? 'default' : 'pointer',
          opacity: desactive ? 0.5 : 1,
        }}
      >
        <CalendarDays size={18} aria-hidden="true" style={{ flexShrink: 0 }} />
        {valeur ? (
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontSize: 15, lineHeight: '20px', fontWeight: 500, whiteSpace: 'nowrap' }}>{libelleDate(valeur)}</span>
            <span style={{ fontSize: 12, lineHeight: '16px', color: 'var(--color-gray-70)' }}>{ecartLisible(aujourdhui, valeur)}</span>
          </span>
        ) : (
          <span style={{ fontSize: 15, color: 'var(--color-gray-70)' }}>Choisir une date</span>
        )}
      </button>
      {effacable && valeur && !desactive && (
        <button
          type="button"
          aria-label={`${libelle} : effacer la date`}
          onClick={() => onChange(null)}
          className="inline-flex items-center justify-center"
          style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', width: 32, height: 32, border: 'none', background: 'transparent', cursor: 'pointer' }}
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
      {ouvert && ancre && (
        <Popover ancre={ancre} titre={libelle} onClose={() => setOuvert(false)} largeur={316} zIndex={zIndex}>
          <Calendrier
            valeur={valeur}
            aujourdhui={aujourdhui}
            min={min}
            max={max}
            raccourcis={raccourcis}
            onChoisir={(j) => {
              onChange(j)
              setOuvert(false)
              ancre.focus()
            }}
          />
        </Popover>
      )}
    </span>
  )
}

function Calendrier({
  valeur,
  aujourdhui,
  min,
  max,
  raccourcis,
  onChoisir,
}: {
  valeur: Jour | null
  aujourdhui: Jour
  min?: Jour | null
  max?: Jour | null
  raccourcis: readonly RaccourciDate[]
  onChoisir: (j: Jour) => void
}): JSX.Element {
  // Le jour qui a le focus du clavier ; le mois affiché le suit.
  const [focus, setFocus] = useState<Jour>(valeur ?? aujourdhui)
  const grille = useMemo(() => grilleMois(focus), [focus])
  const mois = focus.slice(0, 7)
  const feries = useMemo(() => {
    const annees = new Set(grille.map((j) => Number(j.slice(0, 4))))
    return new Set([...annees].flatMap((a) => [...joursFeries(a)]))
  }, [grille])
  const refs = useRef(new Map<Jour, HTMLButtonElement>())
  const clavier = useRef(false)

  useEffect(() => {
    // Le focus suit le jour actif — seulement quand on navigue au clavier (sinon, la souris le garde).
    if (clavier.current) refs.current.get(focus)?.focus()
  }, [focus])
  useEffect(() => {
    // À l'ouverture : focus sur le jour choisi (ou aujourd'hui), pour le clavier.
    refs.current.get(focus)?.focus()
    // Une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const surTouche = (e: KeyboardEvent<HTMLDivElement>): void => {
    const pas: Record<string, (j: Jour) => Jour> = {
      ArrowLeft: (j) => decalerJours(j, -1),
      ArrowRight: (j) => decalerJours(j, 1),
      ArrowUp: (j) => decalerJours(j, -7),
      ArrowDown: (j) => decalerJours(j, 7),
      PageUp: (j) => decalerMois(j, -1),
      PageDown: (j) => decalerMois(j, 1),
      Home: (j) => decalerJours(j, -rangDansSemaine(j)),
      End: (j) => decalerJours(j, 6 - rangDansSemaine(j)),
    }
    const f = pas[e.key]
    if (f) {
      e.preventDefault()
      clavier.current = true
      setFocus(f)
    }
  }

  const fleche: CSSProperties = { width: 36, height: 36, borderRadius: 18, border: 'none', background: 'transparent', cursor: 'pointer' }
  return (
    <div style={{ padding: 12 }} onKeyDown={surTouche}>
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <button type="button" aria-label="Mois précédent" onClick={() => setFocus((j) => decalerMois(j, -1))} className="inline-flex items-center justify-center" style={fleche}>
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        <span aria-live="polite" style={{ fontSize: 15, fontWeight: 600, textTransform: 'capitalize' }}>
          {titreMois(focus)}
        </span>
        <button type="button" aria-label="Mois suivant" onClick={() => setFocus((j) => decalerMois(j, 1))} className="inline-flex items-center justify-center" style={fleche}>
          <ChevronRight size={18} aria-hidden="true" />
        </button>
      </div>

      <div role="grid" aria-label={titreMois(focus)} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {INITIALES_JOURS.map((l, i) => (
          <span key={i} role="columnheader" aria-hidden="true" style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: i >= 5 ? 'var(--color-gray-50)' : 'var(--color-gray-70)', paddingBottom: 4 }}>
            {l}
          </span>
        ))}
        {grille.map((j) => {
          const duMois = j.slice(0, 7) === mois
          const choisi = j === valeur
          const auj = j === aujourdhui
          const ferie = feries.has(j)
          const repos = rangDansSemaine(j) >= 5 || ferie
          const inactif = horsBornes(j, min, max)
          return (
            <button
              key={j}
              ref={(el) => {
                if (el) refs.current.set(j, el)
                else refs.current.delete(j)
              }}
              type="button"
              role="gridcell"
              tabIndex={j === focus ? 0 : -1}
              aria-selected={choisi}
              aria-current={auj ? 'date' : undefined}
              aria-label={`${libelleDate(j)}${ferie ? ', férié' : ''}${auj ? ', aujourd’hui' : ''}`}
              title={ferie ? 'Jour férié' : undefined}
              disabled={inactif}
              onClick={() => onChoisir(j)}
              onFocus={() => setFocus(j)}
              style={{
                height: 38,
                borderRadius: 19,
                border: auj && !choisi ? '1px solid var(--color-black)' : '1px solid transparent',
                backgroundColor: choisi ? 'var(--color-yellow)' : 'transparent',
                color: inactif ? 'var(--color-gray-20)' : !duMois ? 'var(--color-gray-50)' : repos ? 'var(--color-gray-70)' : 'var(--color-black)',
                fontWeight: choisi || auj ? 700 : 400,
                fontSize: 14,
                fontVariantNumeric: 'tabular-nums',
                textDecoration: ferie && duMois ? 'underline dotted' : 'none',
                cursor: inactif ? 'default' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {Number(j.slice(8))}
            </button>
          )
        })}
      </div>

      <div className="flex gap-2" style={{ flexWrap: 'wrap', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--color-gray-10)' }}>
        {[...raccourcis, ...(raccourcis.some((r) => r.jour === aujourdhui) ? [] : [{ libelle: 'Aujourd’hui', jour: aujourdhui }])]
          .filter((r) => !horsBornes(r.jour, min, max))
          .map((r) => (
            <button
              key={r.libelle}
              type="button"
              onClick={() => onChoisir(r.jour)}
              title={libelleDate(r.jour)}
              style={{
                height: 30,
                padding: '0 10px',
                borderRadius: 15,
                border: `1px solid ${r.jour === valeur ? 'var(--color-black)' : 'var(--color-border)'}`,
                backgroundColor: 'var(--color-white)',
                fontSize: 13,
                fontWeight: r.jour === valeur ? 700 : 400,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {r.libelle}
            </button>
          ))}
      </div>
    </div>
  )
}
